import type { Match } from "@/lib/types";

/**
 * Penalty-shootout helpers (migration 0051).
 *
 * A knockout fixture level after regulation is settled on penalties.
 * The shootout decides who progresses — it does NOT change the
 * scoreline. So under normal football convention the match still
 * records as a draw worth one point to each side, and the shootout is
 * reported alongside the record rather than folded into it.
 *
 * That convention is the reason these helpers exist as a separate
 * module rather than inline at each call site: the "a PK win is still a
 * draw" rule is easy to get wrong, and four surfaces render the score.
 * Keeping the rule in one place means the standings math and the score
 * annotations can't drift apart.
 *
 * PK goals never feed goals-for / goals-against / goal difference /
 * clean sheets. Only `pkWins` / `pkLosses` in the team performance
 * tally know about them.
 */

type Result = Pick<
  Match,
  "pk_home" | "pk_away" | "score_home" | "score_away" | "match_status"
> &
  Partial<Pick<Match, "home_team" | "away_team">>;

/** True when this match was decided by a penalty shootout. */
export function hasShootout(match: Result): boolean {
  return (
    match.match_status === "completed" &&
    match.score_home != null &&
    match.score_away != null &&
    Number.isInteger(match.score_home) &&
    Number.isInteger(match.score_away) &&
    match.score_home >= 0 &&
    match.score_home === match.score_away &&
    match.pk_home != null &&
    match.pk_away != null &&
    Number.isInteger(match.pk_home) &&
    Number.isInteger(match.pk_away) &&
    match.pk_home >= 0 &&
    match.pk_away >= 0 &&
    match.pk_home !== match.pk_away
  );
}

/**
 * The shootout annotation for a score line, e.g. `"(4–3 pens)"`.
 * Null when there was no shootout, so callers can render nothing.
 *
 * Uses an en dash to match the score separator used across the app.
 */
export function shootoutLabel(match: Result): string | null {
  if (!hasShootout(match)) return null;
  if (match.home_team && match.away_team) {
    const winner =
      match.pk_home! > match.pk_away! ? match.home_team : match.away_team;
    return `${winner} won ${Math.max(match.pk_home!, match.pk_away!)}–${Math.min(match.pk_home!, match.pk_away!)} on penalties`;
  }
  return `(${match.pk_home}–${match.pk_away} pens)`;
}

/**
 * Which side won the shootout, from the perspective of a given side.
 * Returns null when there was no shootout.
 *
 * `isHome` should come from the caller's existing home/away resolution
 * (e.g. `sideForTeam` on the team matches page) so the orientation
 * matches how the rest of that surface reads the match.
 */
export function shootoutOutcome(
  match: Result,
  isHome: boolean,
): "won" | "lost" | null {
  if (!hasShootout(match)) return null;
  const ours = isHome ? match.pk_home! : match.pk_away!;
  const theirs = isHome ? match.pk_away! : match.pk_home!;
  // A level shootout is rejected by both the form and the API, so this
  // can only be a decisive result.
  return ours > theirs ? "won" : "lost";
}

/**
 * Prose for the shootout winner, e.g. `"Riverside win 4–3 on penalties"`.
 * Used in the edit modal to confirm the outcome before saving, where
 * naming the winner is clearer than a bare tally.
 */
export function shootoutWinnerText(
  pkHome: number | null,
  pkAway: number | null,
  homeTeam: string,
  awayTeam: string,
): string | null {
  if (
    pkHome == null ||
    pkAway == null ||
    !Number.isInteger(pkHome) ||
    !Number.isInteger(pkAway) ||
    pkHome < 0 ||
    pkAway < 0 ||
    pkHome === pkAway
  )
    return null;
  const homeWon = pkHome > pkAway;
  const winner =
    (homeWon ? homeTeam : awayTeam).trim() || (homeWon ? "Home" : "Away");
  const hi = Math.max(pkHome, pkAway);
  const lo = Math.min(pkHome, pkAway);
  return `${winner} win ${hi}–${lo} on penalties`;
}
